/**
 * Serviço para registro e gerenciamento de clientes
 */
import { authApi, formatErrorMessage } from './BaseApiService';

/**
 * Registra um novo cliente pessoa física
 * @param {Object} clientData - Dados do cliente
 * @returns {Promise<Object>} Resposta com dados do cliente registrado
 */
export async function registerIndividualClient(clientData) {
  try {
    const response = await authApi.post('/auth/register/individual', clientData);
    return response.data;
  } catch (error) {
    console.error('[ClientService] Erro no registro de cliente PF:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha no registro de cliente pessoa física');
  }
}

/**
 * Registra um novo cliente pessoa jurídica
 * @param {Object} clientData - Dados do cliente
 * @returns {Promise<Object>} Resposta com dados do cliente registrado
 */
export async function registerEnterpriseClient(clientData) {
  try {
    const response = await authApi.post('/auth/register/enterprise', clientData);
    return response.data;
  } catch (error) {
    console.error('[ClientService] Erro no registro de cliente PJ:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha no registro de cliente pessoa jurídica');
  }
}

/**
 * Atualiza o perfil de um cliente pessoa física
 * @param {string} clientId - ID do cliente
 * @param {Object} updateData - Dados a serem atualizados
 * @returns {Promise<Object>} Resposta com dados do cliente atualizados
 */
export async function updateIndividualProfile(clientId, updateData) {
  try {
    const response = await authApi.put(`/client/update/individual`, updateData);
    return response.data;
  } catch (error) {
    console.error('[ClientService] Erro na atualização de perfil PF:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(
      formatErrorMessage(error) || 'Falha ao atualizar perfil de cliente pessoa física',
    );
  }
}

/**
 * Atualiza o perfil de um cliente pessoa jurídica
 * @param {string} clientId - ID do cliente
 * @param {Object} updateData - Dados a serem atualizados
 * @returns {Promise<Object>} Resposta com dados do cliente atualizados
 */
export async function updateEnterpriseProfile(clientId, updateData) {
  try {
    const response = await authApi.put(`/client/update/enterprise`, updateData);
    return response.data;
  } catch (error) {
    console.error('[ClientService] Erro na atualização de perfil PJ:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(
      formatErrorMessage(error) || 'Falha ao atualizar perfil de cliente pessoa jurídica',
    );
  }
}

/**
 * Obtém os dados do perfil do cliente atual
 * @returns {Promise<Object>} Dados do cliente
 */
export async function getCurrentClientProfile() {
  try {
    const response = await authApi.get('/client/me');
    return response.data;
  } catch (error) {
    console.error('[ClientService] Erro ao obter perfil do cliente:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao obter dados do perfil');
  }
}

/**
 * Verifica se um cliente é do tipo pessoa física
 * @param {Object} clientData - Dados do cliente
 * @returns {boolean} Verdadeiro se é pessoa física
 */
export function isIndividualClient(clientData) {
  // Verifica se tem campos específicos de pessoa física
  return clientData && (clientData.cpf || clientData.full_name);
}

/**
 * Verifica se um cliente é do tipo pessoa jurídica
 * @param {Object} clientData - Dados do cliente
 * @returns {boolean} Verdadeiro se é pessoa jurídica
 */
export function isEnterpriseClient(clientData) {
  // Verifica se tem campos específicos de pessoa jurídica
  return clientData && (clientData.cnpj || clientData.company_name);
}
